import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Sparkles, Loader2, Save, RefreshCw, FileDown, ListPlus } from 'lucide-react';
import api from '../../services/api';
import alertService from '../../utils/alert';
import SchemaForm from './SchemaForm';
import ResultView from './ResultView';
import { unwrap, errMsg, btnPrimary, btnGhost, CATEGORY_LABELS, primaryText, newKey } from './shared';

export default function SkillRunner() {
  const [params] = useSearchParams();
  const [skills, setSkills] = useState(null);
  const [category, setCategory] = useState('all');
  const [skill, setSkill] = useState(null);
  const [values, setValues] = useState({});
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const keyRef = useRef(newKey()); // same key until the user changes the form => a retry cannot run (or bill) twice

  useEffect(() => {
    api.get('/linkedin-ai/skills').then((r) => {
      const list = unwrap(r).skills || [];
      setSkills(list);
      const wanted = params.get('skill');
      const pick = list.find((s) => s.id === wanted);
      if (pick) {
        setSkill(pick);
        const ctx = {};
        ['leadId', 'contactId', 'accountId', 'campaignId', 'dealId'].forEach((k) => { if (params.get(k)) ctx[k] = params.get(k); });
        setValues(ctx);
      }
    }).catch((e) => setError(errMsg(e)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const categories = useMemo(() => ['all', ...new Set((skills || []).map((s) => s.category))], [skills]);
  const visible = (skills || []).filter((s) => category === 'all' || s.category === category);

  const choose = (s) => { setSkill(s); setValues({}); setResult(null); setError(null); keyRef.current = newKey(); };
  const edit = (v) => { setValues(v); keyRef.current = newKey(); };

  const run = async (regenerate = false) => {
    if (regenerate) keyRef.current = newKey();
    setRunning(true); setError(null);
    try {
      const res = await api.post(`/linkedin-ai/skills/${skill.id}/run`, values, { headers: { 'Idempotency-Key': keyRef.current }, timeout: 120000 });
      setResult(unwrap(res));
    } catch (e) { setError(errMsg(e)); } finally { setRunning(false); }
  };

  const saveDraft = async () => {
    const text = primaryText(skill.id, result.output);
    if (!text) return;
    try {
      await api.post('/linkedin-ai/drafts', { title: result.output.title || skill.name, kind: skill.draftKind || 'post', body: text, sourceRunId: result.runId, campaignId: values.campaignId });
      alertService.success('Saved to Content Studio', 'It is a draft. Nothing has been published.');
    } catch (e) { alertService.error('Could not save', errMsg(e)); }
  };

  const exportPdf = async () => {
    const o = result.output;
    const slides = [{ title: o.coverHeadline, body: o.openingHook }, ...o.slides.map((s) => ({ title: s.title, body: s.body })), { title: 'Summary', body: o.finalSummary }, { title: 'Next step', body: o.callToAction }];
    try {
      const res = await api.post('/linkedin-ai/carousel/export', { slides, template: values.template || 'clean' }, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a'); a.href = url; a.download = 'carousel.pdf'; a.click(); URL.revokeObjectURL(url);
    } catch (e) { alertService.error('Export failed', errMsg(e)); }
  };

  const createFollowUp = async (item) => {
    const ok = await alertService.confirm('Create CRM follow-up task?', `${item.followUpNote || item.recommendedCrmNextStep || 'Follow up'} — this creates a real task linked to the selected record.`, 'question', 'Create task');
    if (!(ok?.isConfirmed ?? ok)) return;
    try {
      const r = unwrap(await api.post('/linkedin-ai/followups', { runId: result.runId, title: (item.followUpNote || item.recommendedCrmNextStep || 'LinkedIn follow-up').slice(0, 200), leadId: values.leadId, contactId: values.contactId, confirm: true }));
      alertService.success(r.duplicate ? 'An identical open task already exists' : 'Task created');
    } catch (e) { alertService.error('Could not create task', errMsg(e)); }
  };

  if (!skills) return <div className="sf-card p-8 text-center text-[13px] text-[#5C5C5C]">{error || 'Loading skills…'}</div>;

  const o = result?.output;
  const items = o?.items || [];
  const canFollowUp = (values.leadId || values.contactId) && items.some((i) => i.suggestCrmFollowUp || i.recommendedCrmNextStep);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-4">
      <aside className="space-y-3">
        <div className="flex flex-wrap gap-1">
          {categories.map((c) => (
            <button key={c} onClick={() => setCategory(c)} className={`px-2.5 h-7 rounded-full text-[12px] font-semibold border ${category === c ? 'bg-[#0176D3] text-white border-[#0176D3]' : 'bg-white text-[#444] border-[#D8DDE6]'}`}>
              {c === 'all' ? 'All skills' : CATEGORY_LABELS[c] || c}
            </button>
          ))}
        </div>
        <ul className="space-y-2">
          {visible.map((s) => (
            <li key={s.id}>
              <button onClick={() => choose(s)} className={`w-full text-left sf-card p-3 hover:border-[#0176D3] ${skill?.id === s.id ? 'border-[#0176D3] ring-1 ring-[#0176D3]' : ''}`}>
                <p className="text-[13px] font-bold text-[#2E2E2E]">{s.name}</p>
                <p className="text-[12px] text-[#5C5C5C] mt-0.5">{s.description}</p>
              </button>
            </li>
          ))}
        </ul>
      </aside>

      <section className="space-y-4 min-w-0">
        {!skill ? (
          <div className="sf-card text-center py-16 px-4 text-[13px] text-[#5C5C5C]"><Sparkles className="mx-auto mb-3 text-[#0176D3]" />Pick a skill to start. Skills draft and analyse; they never publish or send anything.</div>
        ) : (
          <>
            <div className="sf-card p-4 space-y-4">
              <div>
                <h2 className="text-[15px] font-bold text-[#2E2E2E]">{skill.name} <span className="text-[11px] font-normal text-[#706E6B]">v{skill.version}</span></h2>
                <p className="text-[12px] text-[#5C5C5C]">{skill.description}</p>
              </div>
              <SchemaForm schema={skill.inputSchema} values={values} onChange={edit} requiresRecord={skill.requiresRecord} />
              <div className="flex items-center gap-2">
                <button className={btnPrimary} disabled={running} onClick={() => run(false)}>
                  {running ? <span className="inline-flex items-center gap-2"><Loader2 size={14} className="animate-spin" />Running…</span> : 'Run skill'}
                </button>
                <span className="text-[11px] text-[#706E6B]">Runs on the platform AI and uses your credits.</span>
              </div>
              {error && <p role="alert" className="text-[12px] text-[#BA0517] bg-[#FDE7E9] border border-[#F6A3AB] rounded px-3 py-2">{error}</p>}
            </div>

            {result && (
              <div className="sf-card p-4 space-y-4">
                <div className="flex flex-wrap items-center gap-2 justify-between">
                  <p className="text-[11px] text-[#706E6B]">
                    {result.replayed ? 'Earlier result (same request) · ' : ''}{result.provider} · {result.model} · {result.usage?.totalTokens ?? 0} tokens · {Math.round((result.durationMs || 0) / 100) / 10}s
                    <span className="ml-2 sf-badge sf-badge-warning">AI suggestion — review before use</span>
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <button className={btnGhost} onClick={() => run(true)} disabled={running}><RefreshCw size={13} className="inline mr-1" />Regenerate</button>
                    {primaryText(skill.id, o) && <button className={btnGhost} onClick={saveDraft}><Save size={13} className="inline mr-1" />Save to Content Studio</button>}
                    {skill.id === 'linkedin.carousel.create' && <button className={btnGhost} onClick={exportPdf}><FileDown size={13} className="inline mr-1" />Export PDF</button>}
                  </div>
                </div>
                <ResultView output={o} />
                {canFollowUp && (
                  <div className="border-t border-[#E5E5E5] pt-3 space-y-2">
                    <p className="text-[12px] font-semibold text-[#444]">Recommended CRM next steps (not done yet)</p>
                    {items.filter((i) => i.suggestCrmFollowUp || i.recommendedCrmNextStep).map((i, n) => (
                      <div key={n} className="flex items-center justify-between gap-3 text-[12px]">
                        <span>{i.followUpNote || i.recommendedCrmNextStep}</span>
                        <button className={btnGhost} onClick={() => createFollowUp(i)}><ListPlus size={13} className="inline mr-1" />Create task…</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </section>
    </div>
  );
}
